import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQueryParams } from "src/hooks/useQueryParams";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import {
  getMyInquiriesAsync,
  ITEMS_PER_PAGE,
} from "src/redux/myInquiries/actions";
import { selectMyInquiries } from "src/redux/myInquiries/selectors";
import { NoMyInquiriesBanner } from "src/page-components/cabinet/my-inquiries/NoMyInquiriesBanner";
import { MY_INQUIRY_SORT_OPTIONS } from "src/page-components/cabinet/my-inquiries/constants";
import { inquiriesTrns } from "src/page-components/inquiries/trns";
import { Link } from "src/components/Link";
import { InquiryCard } from "src/components/InquiryCard";
import { Pagination } from "src/components/Pagination";
import { Loader } from "src/components/Loader";
import { Button } from "src/components/Button";
import { ButtonVariants } from "src/components/Button/types";
import { SortingDropdownList } from "src/components/SortingDropdownList";
import { scrollTop } from "src/utils/scrollTop";
import { QUERY_PARAM_KEYS } from "src/constants/queryParams";
import { PATHNAMES } from "src/constants/routes";
import { ISelectOption } from "src/@types/form";
import { Sizes } from "src/@types/sizes";
import { ReactComponent as PlusIcon } from "src/assets/icons/plus.svg";
import { MetadataWrapper } from "src/components/MetadataWrapper";

const MyInquiries = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { getQueryParam, setQueryParam, setMultipleQueryParams } =
    useQueryParams();

  const { isLoading, data } = useAppSelector(selectMyInquiries);

  const currentPage = Number(getQueryParam(QUERY_PARAM_KEYS.PAGE)) || 1;

  const activeOption = MY_INQUIRY_SORT_OPTIONS.find(
    ({ value }) => value === getQueryParam(QUERY_PARAM_KEYS.STATUS)
  );

  const setOption = ({ value }: ISelectOption) =>
    setMultipleQueryParams({
      [QUERY_PARAM_KEYS.STATUS]: value,
      [QUERY_PARAM_KEYS.PAGE]: 1,
    });

  const setPage = (page: number) => {
    scrollTop();
    setQueryParam(QUERY_PARAM_KEYS.PAGE, page.toString());
  };

  useEffect(() => {
    dispatch(
      getMyInquiriesAsync({
        sorting: activeOption?.value as string,
        page: currentPage,
      })
    );
  }, [dispatch, activeOption, currentPage]);

  if (!data) return null;

  const myInquiriesResult = data?.results || [];

  const pageCount = Math.ceil(data?.count / ITEMS_PER_PAGE);
  const isPaginated = pageCount > 1;

  return (
    <MetadataWrapper name="my_inquiries">
      {isLoading ? (
        <div className="flex justify-center items-center min-h-72">
          <Loader size={Sizes.L} />
        </div>
      ) : (
        <div className="relative flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <SortingDropdownList
              options={MY_INQUIRY_SORT_OPTIONS}
              activeOption={activeOption || MY_INQUIRY_SORT_OPTIONS[0]}
              setOption={setOption}
              isStatus
            />

            <Button variant={ButtonVariants.SECONDARY} className="!p-0">
              <Link
                href={PATHNAMES.CREATE_INQUIRY}
                className="flex items-center justify-center gap-2 py-2 px-5 font-medium"
              >
                <PlusIcon />
                <span>{t(inquiriesTrns.addInquiryButton)}</span>
              </Link>
            </Button>
          </div>

          {/* Inquiries list */}
          <div className="flex flex-col gap-5">
            {myInquiriesResult?.length ? (
              <>
                {myInquiriesResult.map((inquiry) => (
                  <InquiryCard
                    key={inquiry.id}
                    inquiry={inquiry}
                    isReadOnly={false}
                  />
                ))}
                {isPaginated && (
                  <Pagination
                    className="self-center my-8"
                    page={currentPage}
                    pageCount={pageCount}
                    setPage={setPage}
                  />
                )}
              </>
            ) : (
              <NoMyInquiriesBanner />
            )}
          </div>
        </div>
      )}
    </MetadataWrapper>
  );
};

export default MyInquiries;
